/**
 * Authentication API Service
 * @description Backend 인증 API 연동
 */

import type {
    PortalAdminLoginCredentials,
    TenantUserLoginCredentials,
    PortalAdminLoginResponse,
    TenantUserLoginResponse,
} from '@/types/auth.types';
import { apiClient } from '@/utils/httpClient';

/**
 * Portal 관리자 로그인
 */
export async function loginPortalAdmin(
    credentials: Omit<PortalAdminLoginCredentials, 'mode'>
): Promise<PortalAdminLoginResponse> {
    return apiClient.post<PortalAdminLoginResponse>('/admin/auth/login', {
        adminUserId: credentials.adminUserId,
        password: credentials.password,
    }, { skipAuth: true });
}

/** 로그인 1단계 후 MFA 필요 시 응답 */
export interface TenantUserLoginMfaRequiredResponse {
    requireMfa: true;
    mfaChallengeId: string;
    userId: string;
    tenantId: string;
    message?: string;
}

/**
 * Tenant 사용자 로그인 (비밀번호만). MFA 필요 시 requireMfa + mfaChallengeId 반환.
 */
export async function loginTenantUser(
    credentials: Omit<TenantUserLoginCredentials, 'mode'>
): Promise<TenantUserLoginResponse | TenantUserLoginMfaRequiredResponse> {
    return apiClient.post<TenantUserLoginResponse | TenantUserLoginMfaRequiredResponse>('/auth/login', {
        tenantId: credentials.tenantId,
        userId: credentials.userId,
        password: credentials.password,
    }, { skipAuth: true });
}

/**
 * Tenant 사용자 MFA 2단계 검증 (OTP 코드 제출 후 토큰 발급)
 */
export async function verifyTenantMfa(
    mfaChallengeId: string,
    code: string
): Promise<TenantUserLoginResponse> {
    return apiClient.post<TenantUserLoginResponse>('/auth/mfa/verify', {
        mfaChallengeId,
        code: code.trim(),
    }, { skipAuth: true });
}

/** MFA TOTP 설정 시작 응답 (QR/수동 입력용) */
export interface TenantMfaSetupResponse {
    secret: string;
    qrUrl: string;
    issuer: string;
    message?: string;
}

/**
 * Tenant 사용자 MFA TOTP 설정 시작 (로그인 후 호출, JWT 필요)
 */
export async function setupTenantMfa(): Promise<TenantMfaSetupResponse> {
    return apiClient.post<TenantMfaSetupResponse>('/auth/mfa/setup', {});
}

/**
 * Tenant 사용자 MFA TOTP 등록 확인 (6자리 코드 검증 후 enrolled 저장, JWT 필요)
 */
export async function confirmTenantMfa(code: string): Promise<{ success: boolean; message: string }> {
    return apiClient.post<{ success: boolean; message: string }>('/auth/mfa/confirm', {
        code: code.trim(),
    });
}

/**
 * Tenant 사용자 비밀번호 변경 (로그인 후 호출, JWT 필요)
 */
export async function changeTenantPassword(
    currentPassword: string,
    newPassword: string
): Promise<{ success: boolean; message: string }> {
    return apiClient.post<{ success: boolean; message: string }>('/auth/password/change', {
        currentPassword,
        newPassword,
    });
}

/**
 * Portal 관리자 정보 조회
 */
export async function getPortalAdminMe(token: string): Promise<PortalAdminLoginResponse> {
    return apiClient.get<PortalAdminLoginResponse>('/admin/auth/me');
}

/**
 * Tenant 사용자 정보 조회
 */
export async function getTenantUserMe(token: string, tenantId: string): Promise<TenantUserLoginResponse> {
    return apiClient.get<TenantUserLoginResponse>('/auth/me', {
        headers: {
            'X-Tenant-Id': tenantId,
        },
    });
}

/**
 * 권한 체크
 */
export interface AuthzCheckRequest {
    serviceId: string;
    action: string;
}

export interface AuthzCheckResponse {
    tenantId: string;
    userId: string;
    roles: string[];
    allowed: boolean;
}

export async function checkPermission(
    tenantId: string,
    serviceId: string,
    action: string = 'view'
): Promise<AuthzCheckResponse> {
    return apiClient.post<AuthzCheckResponse>(`/tenants/${tenantId}/authz/check`, {
        serviceId,
        action,
    });
}

/**
 * Batch 권한 체크
 */
export interface AuthzCheckBatchResponse {
    tenantId: string;
    userId: string;
    roles: string[];
    groups: string[];
    results: { serviceId: string; action: string; allowed: boolean; menuVisible?: boolean }[];
}

export async function checkPermissionBatch(
    tenantId: string,
    checks: { serviceId: string; action: string }[]
): Promise<AuthzCheckBatchResponse> {
    const raw = await apiClient.post<AuthzCheckBatchResponse | { data?: AuthzCheckBatchResponse }>(
        `/tenants/${tenantId}/authz/check-batch`,
        { checks },
    );
    const body =
        raw && typeof raw === 'object' && Array.isArray((raw as AuthzCheckBatchResponse).results)
            ? (raw as AuthzCheckBatchResponse)
            : (raw as { data?: AuthzCheckBatchResponse })?.data;
    const results = Array.isArray(body?.results) ? body.results : [];
    return {
        tenantId: body?.tenantId ?? tenantId,
        userId: body?.userId ?? '',
        roles: body?.roles ?? [],
        groups: body?.groups ?? [],
        results,
    };
}